import { useMemo } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { useTranslation } from 'react-i18next';

export default function FieldConfiguration({ field, updateField, removeField, getFieldTypeLabel, collections = [] }) {
  const { t } = useTranslation();
  const selectedCollection = useMemo(() => {
    return collections.find(col => col.id === field.lookupCollectionId || col.name === field.lookupCollectionName);
  }, [collections, field.lookupCollectionId, field.lookupCollectionName]);

  const resolvedCollectionId = field.lookupCollectionId || selectedCollection?.id || '';
  const collectionFields = selectedCollection?.fields || [];
  const selectableFields = [{ name: '_id' }, ...collectionFields];

  const handleTypeChange = (e) => {
    const nextType = e.target.value;
    if (nextType === 'lookup') {
      updateField(field.id, { type: nextType });
    } else {
      updateField(field.id, {
        type: nextType,
        lookup: undefined,
        lookupCollectionId: '',
        lookupCollectionName: '',
        lookupLinkField: '',
        lookupDisplayField: ''
      });
    }
  };

  return (
    <div className="rounded-lg bg-white border border-gray-200 p-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-12 items-start">
        <div className="sm:col-span-4">
          <label className="block text-xs font-medium text-gray-700 mb-1">{t('customCollections.form.fieldLabels.name')}</label>
          <input
            type="text"
            value={field.name}
            onChange={(e) => updateField(field.id, { name: e.target.value })}
            className="block w-full rounded-md bg-white px-3 py-1.5 text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
          />
        </div>

        <div className="sm:col-span-3">
          <label className="block text-xs font-medium text-gray-700 mb-1">{t('customCollections.form.fieldLabels.type')}</label>
          <div className="grid grid-cols-1">
            <select
              value={field.type}
              onChange={handleTypeChange}
              className="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-1.5 pr-8 pl-3 text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
            >
              <option value="string">{t('customCollections.form.fieldTypes.string')}</option>
              <option value="integer">{t('customCollections.form.fieldTypes.integer')}</option>
              <option value="editor">{t('customCollections.form.fieldTypes.editor')}</option>
              <option value="lookup">{t('customCollections.form.fieldTypes.lookup')}</option>
            </select>
            <ChevronDownIcon
              aria-hidden="true"
              className="pointer-events-none col-start-1 row-start-1 mr-2 size-4 self-center justify-self-end text-gray-400"
            />
          </div>
        </div>

        {field.type === 'lookup' && (
          <>
            <div className="sm:col-span-3">
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('customCollections.form.lookup.targetCollection')}</label>
              <div className="grid grid-cols-1">
                <select
                  value={resolvedCollectionId}
                  onChange={(e) => {
                    const collection = collections.find(col => col.id === e.target.value);
                    updateField(field.id, {
                      lookupCollectionId: e.target.value,
                      lookupCollectionName: collection?.name || '',
                      lookupLinkField: '',
                      lookupDisplayField: ''
                    });
                  }}
                  className="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-1.5 pr-8 pl-3 text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                >
                  <option value="">{t('customCollections.form.lookup.selectCollection')}</option>
                  {collections.map(col => (
                    <option key={col.id} value={col.id}>{col.name}</option>
                  ))}
                </select>
                <ChevronDownIcon
                  aria-hidden="true"
                  className="pointer-events-none col-start-1 row-start-1 mr-2 size-4 self-center justify-self-end text-gray-400"
                />
              </div>
            </div>

            <div className="sm:col-span-3">
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('customCollections.form.lookup.linkField')}</label>
              <div className="grid grid-cols-1">
                <select
                  value={field.lookupLinkField || ''}
                  onChange={(e) => updateField(field.id, { lookupLinkField: e.target.value })}
                  disabled={!field.lookupCollectionId}
                  className="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-1.5 pr-8 pl-3 text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 disabled:bg-gray-100"
                >
                  <option value="">{t('customCollections.form.lookup.selectField')}</option>
                  {selectableFields.map(colField => (
                    <option key={colField.name} value={colField.name}>{colField.name}</option>
                  ))}
                </select>
                <ChevronDownIcon
                  aria-hidden="true"
                  className="pointer-events-none col-start-1 row-start-1 mr-2 size-4 self-center justify-self-end text-gray-400"
                />
              </div>
            </div>

            <div className="sm:col-span-3">
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('customCollections.form.lookup.displayField')}</label>
              <div className="grid grid-cols-1">
                <select
                  value={field.lookupDisplayField || ''}
                  onChange={(e) => updateField(field.id, { lookupDisplayField: e.target.value })}
                  disabled={!field.lookupCollectionId}
                  className="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-1.5 pr-8 pl-3 text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 disabled:bg-gray-100"
                >
                  <option value="">{t('customCollections.form.lookup.selectField')}</option>
                  {selectableFields.map(colField => (
                    <option key={colField.name} value={colField.name}>{colField.name}</option>
                  ))}
                </select>
                <ChevronDownIcon
                  aria-hidden="true"
                  className="pointer-events-none col-start-1 row-start-1 mr-2 size-4 self-center justify-self-end text-gray-400"
                />
              </div>
            </div>

          </>
        )}

        <div className="sm:col-span-2 self-center">
          <label className="flex items-center space-x-2 pt-4">
            <input
              type="checkbox"
              checked={field.required}
              onChange={(e) => updateField(field.id, { required: e.target.checked })}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-600 border-gray-300 rounded"
            />
            <span className="text-xs font-medium text-gray-700">{t('customCollections.form.fieldFlags.required')}</span>
          </label>
        </div>

        <div className="sm:col-span-2 self-center pt-4">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={field.filterpresent}
              onChange={(e) => updateField(field.id, { filterpresent: e.target.checked })}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-600 border-gray-300 rounded"
            />
            <span className="text-xs font-medium text-gray-700">{t('customCollections.form.fieldFlags.filterable')}</span>
          </label>
        </div>

        <div className="sm:col-span-1 flex justify-end">
          <button
            type="button"
            onClick={() => removeField(field.id)}
            className="rounded p-1.5 text-red-600 hover:bg-red-100 transition-colors"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="mt-2">
        <span className="text-xs text-gray-500">
          {t('customCollections.form.summary.field', { type: getFieldTypeLabel(field.type) })}
          {field.required && ` (${t('customCollections.form.summary.required')})`}
          {field.filterpresent && ` (${t('customCollections.form.summary.filterable')})`}
          {field.type === 'lookup' && field.lookupCollectionName && field.lookupLinkField && (
            ` -> ${field.lookupCollectionName}.${field.lookupLinkField}${field.lookupDisplayField ? ` (${t('customCollections.form.summary.label')}: ${field.lookupDisplayField})` : ''}`
          )}
        </span>
      </div>
    </div>
  );
}
